import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Download, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface SimplePDFViewerProps {
  fileUrl: string;
  fileName?: string;
  className?: string;
}

export const SimplePDFViewer: React.FC<SimplePDFViewerProps> = ({ 
  fileUrl, 
  fileName = 'document.pdf',
  className = ''
}) => {
  const { toast } = useToast();

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "Download started",
      description: `Downloading ${fileName}`,
    });
  };

  const openInNewTab = () => {
    window.open(fileUrl, '_blank');
  };

  return (
    <div className={className}>
      <Card className="overflow-hidden">
        {/* Header */}
        <div className="border-b bg-muted/30 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-red-600" />
            <span className="font-medium text-sm">{fileName}</span>
            <Badge variant="secondary" className="ml-2">PDF</Badge>
          </div>
          
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={openInNewTab}>
              <ExternalLink className="h-4 w-4 mr-1" />
              Open
            </Button>
            <Button size="sm" variant="default" onClick={handleDownload}>
              <Download className="h-4 w-4 mr-1" />
              Download
            </Button>
          </div>
        </div>

        {/* PDF Viewer */}
        <div className="w-full bg-gray-100" style={{ height: 'calc(100% - 120px)' }}>
          <iframe
            src={`${fileUrl}#toolbar=1&navpanes=0&scrollbar=1`}
            className="w-full h-full border-0"
            title={fileName}
            onError={() => {
              toast({
                title: "PDF viewing issue",
                description: "If the PDF doesn't display, try opening in a new tab or downloading it.",
                variant: "destructive"
              });
            }}
          />
        </div>

        {/* Fallback message */}
        <div className="p-4 bg-muted/50 text-center text-sm text-muted-foreground">
          <p>
            If the PDF doesn't display properly, try opening it in a new tab or downloading it.
            Some browsers may block PDF viewing from external sources.
          </p>
        </div>
      </Card>
    </div>
  );
};